/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import classNames from 'classnames/bind';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import Utils from 'yoda-core-components/lib/helpers/Utils/Utils';
import Image from './Image';
import styles from './Swatches.css';

const cx = classNames.bind(styles);

const Swatches = ({
    activeSwatchImageId,
    handleSwatchDesktopEvent,
    handleSwatchMobileEvent,
    isEnsembledProduct,
    productCardWidth,
    productUrl,
    setGalleryScrollInLocalStorage,
    skuSwatch: swatches,
}) => {
    const {
        deviceType: { isDesktop = true } = {},
        featureFlags: { enableSwatches = true } = {},
        preferences: { swatches: { itemsPerRow = 6 } = {} } = {},
    } = useSelector(({ context = {} }) => context);

    const [maxSwatchesPerRow, setMaxSwatchesPerRow] = useState(itemsPerRow);
    const [activeSwatch, setActiveSwatch] = useState(null);
    const swatchesToShow = useMemo(
        () =>
            swatches.slice(
                0,
                // Account for extra swatches button
                swatches.length > maxSwatchesPerRow ? maxSwatchesPerRow - 1 : maxSwatchesPerRow
            ),
        [maxSwatchesPerRow, swatches]
    );

    const swatchSize = isDesktop ? 20 : 18;

    useEffect(() => {
        let swatchesPerRow = itemsPerRow;

        if (productCardWidth > 0)
            swatchesPerRow = Math.floor(productCardWidth / (swatchSize + 8)) || itemsPerRow;

        setMaxSwatchesPerRow(swatchesPerRow);
    }, [productCardWidth]);

    useEffect(() => {
        const nextActiveSwatch = swatchesToShow.find(
            (swatch) => swatch.swatchImageId === activeSwatchImageId
        );

        nextActiveSwatch && setActiveSwatch(nextActiveSwatch);
    }, [activeSwatchImageId, maxSwatchesPerRow, swatchesToShow]);

    const navigateToPdp = () => {
        const previouslySelectedSwatchId = LocalStorage.getData('selectedSwatchId');
        let pdpUrl = productUrl;

        if (isDesktop && previouslySelectedSwatchId && previouslySelectedSwatchId.length) {
            pdpUrl = Utils.updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                previouslySelectedSwatchId
            );
            LocalStorage.removeData('selectedSwatchId');
        }

        LocalStorage.setData('fromGallery', { scrollTo: 'swatch' });
        setGalleryScrollInLocalStorage();
        window.location.assign(pdpUrl);
    };

    const visibleSwatches = swatchesToShow.map((currentSwatch) => {
        const { colorName, swatchImageId } = currentSwatch;
        const isSwatchSelected = activeSwatch && swatchImageId === activeSwatch.swatchImageId;
        const config = {
            button: {
                className: cx('ml-1', 'relative', {
                    active: isSwatchSelected,
                }),
                key: `${colorName}-${swatchImageId}-${Math.random(Date.now())}`,
                onClick: isDesktop
                    ? (event) => handleSwatchDesktopEvent(event, currentSwatch)
                    : () => handleSwatchMobileEvent(currentSwatch),
                onFocus: isDesktop
                    ? (event) => handleSwatchDesktopEvent(event, currentSwatch)
                    : null,
                onMouseLeave: isDesktop
                    ? (event) =>
                          handleSwatchDesktopEvent(
                              event,
                              isEnsembledProduct ? currentSwatch : activeSwatch
                          )
                    : null,
                onMouseOver: isDesktop
                    ? (event) => handleSwatchDesktopEvent(event, currentSwatch)
                    : null,
                style: {
                    height: swatchSize,
                    width: swatchSize,
                },
            },
            image: {
                alt: colorName,
                iconSize: swatchSize,
                isSwatchSelected,
                swatchImageId,
            },
        };

        return (
            <button
                {...config.button}
                data-value={isSwatchSelected ? 'active' : 'not-active'}
                key={swatchImageId}
                type="button"
            >
                <Image {...config.image} />
            </button>
        );
    });

    if (enableSwatches && swatches.length) {
        const showExtraSwatchesButton = swatches.length > maxSwatchesPerRow;
        const swatchesConfig = {
            extraSwatches: {
                button: {
                    'aria-label': `View ${
                        swatches.length - maxSwatchesPerRow
                    } additional swatches on product page`,
                    className: cx(
                        'additional-swatches',
                        'flex',
                        'font-open-sans-semibold',
                        'items-center',
                        'mx-1',
                        'text-xsmall'
                    ),
                    onClick: navigateToPdp,
                    style: {
                        height: swatchSize,
                        width: swatchSize,
                    },
                },
                icon: {
                    className: 'font-open-sans-bold text-medium',
                },
            },
            wrapper: {
                className: cx('flex', 'relative', 'swatches', {
                    'pt-1': isDesktop,
                    'pt-half': !isDesktop,
                }),
                'data-automation-id': 'color-availability',
            },
        };

        return (
            <div {...swatchesConfig.wrapper}>
                {visibleSwatches}
                {showExtraSwatchesButton && (
                    <button {...swatchesConfig.extraSwatches.button} type="button">
                        <span {...swatchesConfig.extraSwatches.icon}>+</span>
                        {swatches.length - (maxSwatchesPerRow - 1)}
                    </button>
                )}
            </div>
        );
    }

    return null;
};

Swatches.propTypes = {
    activeSwatchImageId: string.isRequired,
    handleSwatchDesktopEvent: func.isRequired,
    handleSwatchMobileEvent: func.isRequired,
    isEnsembledProduct: bool.isRequired,
    setGalleryScrollInLocalStorage: func.isRequired,
    skuSwatch: arrayOf(
        shape({
            colorName: string,
            colorizedImageId: string,
            defaultSwatch: bool,
            skuAltImages: arrayOf(
                shape({
                    imgId: string,
                    colorSeq: number,
                })
            ),
            skuId: string,
            swatchImageId: string,
        })
    ).isRequired,
    productCardWidth: number,
    productUrl: string,
};

Swatches.defaultProps = {
    productCardWidth: 0,
    productUrl: '',
};

export default Swatches;
